import { supabase } from '../lib/supabase';

export interface LmdRate {
  id: number;
  country_code: string;
  rate_type: 'ex_diesel' | 'incl_diesel';
  pallets: number;
  rate_eur: number;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface LmdCalculation {
  baseRate: number;
  dieselSurcharge: number;
  totalRate: number;
  palletCount: number;
  description: string;
}

export interface LmdSetting {
  id: number;
  setting_key: string;
  setting_value: number;
  setting_type: 'percentage' | 'fixed' | 'boolean';
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Fetch all LMD rates for a specific country and rate type
 */
export async function getLmdRates(
  countryCode: string, 
  rateType: 'ex_diesel' | 'incl_diesel' = 'ex_diesel'
): Promise<LmdRate[]> {
  const { data, error } = await supabase
    .from('lmd_rates')
    .select('*')
    .eq('country_code', countryCode.toUpperCase())
    .eq('rate_type', rateType)
    .eq('is_active', true)
    .order('pallets');

  if (error) {
    console.error('Error fetching LMD rates:', error);
    throw error;
  }

  return data || [];
}

/**
 * Get specific LMD rate for exact pallet count
 */
export async function getLmdRate(
  countryCode: string,
  palletCount: number,
  rateType: 'ex_diesel' | 'incl_diesel' = 'ex_diesel'
): Promise<LmdRate | null> {
  const { data, error } = await supabase
    .from('lmd_rates')
    .select('*')
    .eq('country_code', countryCode.toUpperCase())
    .eq('rate_type', rateType)
    .eq('pallets', palletCount)
    .eq('is_active', true)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No data found
      return null;
    }
    console.error('Error fetching LMD rate:', error);
    throw error;
  }

  return data;
}

/**
 * Calculate LMD cost for given parameters
 */
export async function calculateLmdCost(
  countryCode: string,
  palletCount: number,
  cartonCount: number = 0,
  includeDieselSurcharge: boolean = false,
  dieselSurchargePercent: number = 15,
  forceCartonRate: boolean = false
): Promise<LmdCalculation> {
  try {
    let rate: LmdRate | null = null;
    let baseRate = 0;
    let description = '';
    let actualPalletCount = palletCount;

    // Use carton rate for small shipments or when forced
    if ((cartonCount > 0 && cartonCount < 8) || forceCartonRate) {
      // Get carton rate (stored as pallets = 0)
      rate = await getLmdRate(countryCode, 0, 'ex_diesel');
      if (rate) {
        baseRate = rate.rate_eur * cartonCount;
        description = `LMD ${countryCode} (${cartonCount} carton${cartonCount !== 1 ? 's' : ''})`;
        actualPalletCount = 1; // For billing purposes, treat as 1 unit
      }
    } 
    
    // Use pallet rate for larger shipments
    if (!rate) {
      // Determine effective pallet count (max 15 for rate lookup)
      const ratePalletCount = Math.min(palletCount, 15);
      
      // Get the base rate (always ex_diesel from table)
      rate = await getLmdRate(countryCode, ratePalletCount, 'ex_diesel');
      
      if (rate) {
        baseRate = rate.rate_eur;
        description = `LMD ${countryCode} (${palletCount} pallet${palletCount !== 1 ? 's' : ''})`;
        if (cartonCount > 0) {
          description += ` (${cartonCount} cartons)`;
        }
        if (palletCount > 15) {
          description += ` - using 15+ rate`;
        }
      }
    }
    
    if (!rate) {
      throw new Error(`No LMD rate found for ${countryCode} with ${palletCount} pallets or ${cartonCount} cartons`);
    }

    const dieselSurcharge = includeDieselSurcharge ? (baseRate * dieselSurchargePercent / 100) : 0;
    const totalRate = baseRate + dieselSurcharge;

    // Add diesel info to description
    if (includeDieselSurcharge) {
      description += ` + ${dieselSurchargePercent}% diesel`;
    }

    return {
      baseRate,
      dieselSurcharge,
      totalRate,
      palletCount: actualPalletCount,
      description
    };
  } catch (error) {
    console.error('Error calculating LMD cost:', error);
    throw error;
  }
}

/**
 * Get all available countries with LMD rates
 */
export async function getLmdCountries(): Promise<string[]> {
  const { data, error } = await supabase
    .from('lmd_rates')
    .select('country_code')
    .eq('is_active', true)
    .order('country_code');

  if (error) {
    console.error('Error fetching LMD countries:', error);
    throw error;
  }

  // Return unique country codes
  const countries = [...new Set(data?.map(item => item.country_code) || [])];
  return countries;
}

/**
 * Admin function to update LMD rate
 */
export async function updateLmdRate(
  id: number,
  updates: Partial<Pick<LmdRate, 'rate_eur' | 'is_active' | 'notes'>>
): Promise<LmdRate> {
  const { data, error } = await supabase
    .from('lmd_rates')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating LMD rate:', error);
    throw error;
  }

  return data;
}

/**
 * Admin function to create new LMD rate
 */
export async function createLmdRate(
  rate: Omit<LmdRate, 'id' | 'created_at' | 'updated_at'>
): Promise<LmdRate> {
  const { data, error } = await supabase
    .from('lmd_rates')
    .insert([rate])
    .select()
    .single();

  if (error) {
    console.error('Error creating LMD rate:', error);
    throw error;
  }

  return data;
}

/**
 * Get LMD setting by key
 */
export async function getLmdSetting(settingKey: string): Promise<LmdSetting | null> {
  const { data, error } = await supabase
    .from('lmd_settings')
    .select('*')
    .eq('setting_key', settingKey)
    .eq('is_active', true)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No data found
      return null;
    }
    console.error('Error fetching LMD setting:', error);
    throw error;
  }

  return data;
}

/**
 * Get all LMD settings
 */
export async function getAllLmdSettings(): Promise<LmdSetting[]> {
  const { data, error } = await supabase
    .from('lmd_settings')
    .select('*')
    .eq('is_active', true)
    .order('setting_key');

  if (error) {
    console.error('Error fetching LMD settings:', error);
    throw error;
  }

  return data || [];
}

/**
 * Update LMD setting
 */
export async function updateLmdSetting(
  id: number,
  updates: Partial<Pick<LmdSetting, 'setting_value' | 'description' | 'is_active'>>
): Promise<LmdSetting> {
  const { data, error } = await supabase
    .from('lmd_settings')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating LMD setting:', error);
    throw error;
  }

  return data;
}

/**
 * Get current diesel surcharge percentage from settings
 */
export async function getDieselSurchargePercentage(): Promise<number> {
  const setting = await getLmdSetting('diesel_surcharge_percentage');
  return setting?.setting_value || 15; // Default fallback
}

/**
 * Get cartons per pallet setting
 */
export async function getCartonsPerPallet(): Promise<number> {
  const setting = await getLmdSetting('cartons_per_pallet');
  return setting?.setting_value || 8; // Default fallback
}

/**
 * Get pallet threshold setting
 */
export async function getPalletThreshold(): Promise<number> {
  const setting = await getLmdSetting('pallet_threshold');
  return setting?.setting_value || 7; // Default fallback
} 